import {
    CompanySettings, DateRange, DisplayType, DisplayValue, HorizontalAlignment, LicenseTester, LogManager, ModelRow,
    Navigation, StringUtil, UrlUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { CommonDialogs, CommonTooltips, ImageTable } from "@mcleod/common";
import {
    Button, ButtonVariant, ChangeEvent, ClickEvent, Component, ContextMenuCreationEvent, DataDisplayEvent,
    DataSourceExecutionEvent, DataSourceMode, DataSourceRemoteDataChangeEvent, EditRowDecorator, Image, KeyEvent, Label,
    Layout, ModelDataChangeType, Panel, SlideoutDecorator, Snackbar, Tab, Table, TableRow, TableRowDisplayEvent,
    TableRowExpansionEvent, Tabset, Textbox, ValidationResult
} from "@mcleod/components";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { isIntercompanyEnabled } from "@mcleod/datafusion/src/EdiUtility";
import { getDetentionControlBoolean, LMEUserSettings } from "@mcleod/general";
import {
    displayBrkStatusDropDowns, populateAndDisplayBrkStatusDropDowns, setCommodityTooltipContent, setupBrkStatusPanel
} from "@mcleod/powerbroker/src/BrokeragePlanningUtil";
import { BrTrackingQuickInfoPanel } from "@mcleod/powerbroker/src/tracking/BrTrackingQuickInfoPanel";
import { ModelCsmProfile, RowCsmProfile } from "../../../planning/csm/src/models/ModelCsmProfile";
import { ManifestMovementQuickInfo } from "../../ManifestMovementQuickInfo";
import { MultiStop } from "../../MultiStop";
import { OrderTabView } from "../../OrderTabView";
import { ShipmentStatusSlideout } from "../../ShipmentStatusSlideout";
import { FilterPanel, SecondaryFilter } from "../FilterPanel";
import { PlanningUtil } from "../PlanningUtil";
import { AutogenLayoutCustomerService } from "./autogen/AutogenLayoutCustomerService";
import { CustomerServiceProfileFilterer } from "./CustomerServiceProfileFilter";
import { CustomerServiceSecondaryFilter } from "./CustomerServiceSecondaryFilter";
import { CustomerServiceToolPanel } from "./CustomerServiceToolPanel";
import { UploadImages } from "@mcleod/common/src/imaging/UploadImages";

const log = LogManager.getLogger("lme.planning.csm.CustomerService");
const SHIPMENT_STATUS = "S";

export class CustomerService extends AutogenLayoutCustomerService {
    private timerId: number;
    private _profileRow: RowCsmProfile;
    private _customerServiceProfile: string;
    private filterObject: CustomerServiceProfileFilterer;
    private filterPanel: FilterPanel;
    private shipmentStatusError: boolean;
    private isCompanyTypeBoth: boolean;
    private showDetention: boolean = getDetentionControlBoolean("is_active", false);

    onLoad(): void | Promise<void> {
        this.mainDataSource.orderBy = [{ field: "origin_early", sort: "asc" }];
        this.tableCustomerServiceMovements.overrideUrlParams = (selectedRow: TableRow) => {
            return { key: selectedRow.data.get("movement_id"), orderId: selectedRow.data.get("order_id") }
        };
        this.setupMinuteTimer();
        const filters = [
            CustomerServiceSecondaryFilter.APPT_REQUIRED,
            CustomerServiceSecondaryFilter.APPT_CONFIRMED,
            CustomerServiceSecondaryFilter.APPOINTMENT_LATE,
            CustomerServiceSecondaryFilter.ARRIVED_AT_SHIPPER,
            CustomerServiceSecondaryFilter.ARRIVED_AT_CONSIGNEE,
            CustomerServiceSecondaryFilter.SHIPMENT_STATUS_ERRORS,
            CustomerServiceSecondaryFilter.INTERCOMPANY,
            CustomerServiceSecondaryFilter.SERVICE_FAILURES,
            CustomerServiceSecondaryFilter.ON_HOLD,
            CustomerServiceSecondaryFilter.LOADS_NOT_ASSIGNED,
            CustomerServiceSecondaryFilter.LOADS_NOT_RATED,
            CustomerServiceSecondaryFilter.NOT_READY_TO_BILL,
            CustomerServiceSecondaryFilter.MISSING_BOL,
            CustomerServiceSecondaryFilter.TRACKING_LATE
        ];
        if (this.showDetention) {
            filters.push(
                CustomerServiceSecondaryFilter.FILTER_FREE_TIME_STARTED,
                CustomerServiceSecondaryFilter.FILTER_DETENTION_CHARGING,
                CustomerServiceSecondaryFilter.FILTER_DETENTION_OCCURRED,
                CustomerServiceSecondaryFilter.FILTER_DETENTION_CLOSED
            );
        }

        const isEdiLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.edi");
        const newFilters = isEdiLicensed ? filters : filters.filter(filter => filter.id !== CustomerServiceSecondaryFilter.SHIPMENT_STATUS_ERRORS.id);
        this.filterPanel = new FilterPanel(newFilters, this.tableCustomerServiceMovements, this.panelSecondaryFilters, this.onClickDataDisplay);
        this.applyUrlParams();
        this.setSecondaryFilters();

        const companySettings = CompanySettings.get();
        const isAsset = companySettings?.is_asset;
        const isBrokerage = companySettings?.is_brokerage;
        this.isCompanyTypeBoth = isAsset && isBrokerage;
    }

    get customerServiceProfile(): string {
        return this._customerServiceProfile;
    }

    set customerServiceProfile(value: string) {
        this._customerServiceProfile = value;
    }

    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile")
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile as RowCsmProfile);
            return;
        }
        profile = profile ?? this.customerServiceProfile ?? LMEUserSettings.get()?.csm_profile;
        if (profile == null) {
            Snackbar.showSnackbar("You do not have a default Customer Service profile defined. "
                + "Add a default profile to your preferences so your orders will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        } else {
            const profileRow = await new ModelCsmProfile().searchSingle({ id: profile });
            if (profileRow == null) {
                Snackbar.showSnackbar("The profile [" + profile + "] was not found.", { persist: true });
            } else {
                log.debug("Order Planning Profile", profileRow);
                this.setProfileRow(profileRow);
            }
        }

        const icTaskFilterComponent = this.findComponentById('btn' + CustomerServiceSecondaryFilter.INTERCOMPANY.id);
        if (await isIntercompanyEnabled()) {
            if(icTaskFilterComponent) {
                icTaskFilterComponent.visible = true;
            }
        }
        else{
            if(icTaskFilterComponent) {
                icTaskFilterComponent.visible = false;
            }
        }

    }

    public get profileRow(): RowCsmProfile {
        return this._profileRow;
    }

    public setProfileRow(value: RowCsmProfile) {
        if (value != this._profileRow) {
            this._profileRow = value;
            this._profileRow.resetOriginalData();
            this.filterObject = new CustomerServiceProfileFilterer(value);
            if (this._profileRow != null) {
                this.mainDataSource.search({
                    profile_row: this.profileRow
                })
                this.textboxDateRange.text = DateRange.formatWithDaysOut(this._profileRow.get("start_day"), this._profileRow.get("orders_days"));
                this.textboxDateRange.visible = !this.textboxDateRange.isEmpty();
            }
        }
    }

    override get windowTitle(): string {
        const title = "Customer Service";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    tableCustomerServiceMovementsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.onRowDataDisplay(tableRow);
    }

    async tableCustomerServiceOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const expandComponent = await tableRow.getExpansionComponentLoaded() as Panel;
        const imageTable = expandComponent.findComponentById("layoutImageTable") as ImageTable;
        const tabset = expandComponent.findComponentById("tabsetCustomerServiceExpand") as Tabset;
        const orderId = tableRow?.data.get("order_id");

        if (tabset && Array.isArray(tabset.components) && !tabset.components.some((tab: Tab) => tab.id === "tabUploadImage")) {
            const uploadButton = new Button({
                id: "buttonUploadImageTab",
                caption: "Upload Image",
                color: "primary",
                imageName: ImageName.pictureOutlined,
                variant: ButtonVariant.text,
                fontBold: true,
                fontSize: "medium",
                onClick: async () => {
                    const layout = Layout.getLayout("common/imaging/UploadImages", {
                        maxWidth: 1100,
                        width: window.innerWidth * .55,
                        backgroundColor: "defaultBackground",
                        borderRadius: 4,
                        borderWidth: 1,
                        borderShadow: true,
                        borderColor: "strokeSecondary"
                    }) as UploadImages;
                    layout.setup("O", orderId, null, (rowId: string) => imageDialog.close());
                    const imageDialog = CommonDialogs.createDialog(layout, { title: "Upload Images", okVisible: false });
                    await imageDialog.show();
                    if (layout.addedAnImage === true && imageTable) {
                        imageTable.populateForRow("O", orderId);
                    }
                }
            });

            tabset.addCenterTool(uploadButton);
            imageTable.buttonUploadImage.visible = false;
        }

         if (orderId) {
             imageTable.populateForRow("O", orderId);
         }
     }

    orderNumberOnClick(event: ClickEvent) {
        OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
    }

    labelManifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    setTooltipContent(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "labelCommodityDescr": {
                return setCommodityTooltipContent(comp, modelRow);
            }
            case "labelTrailerType": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("trailer_type"), "lme/dispatch/EquipmentTypeQuickInfo");
            }
            case "labelCustomerName": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo")
            }
            case "textboxPlanningComment": {
                comp.tooltipDynamicContent = () => {
                    return modelRow.get("planning_comment");
                };
                return;
            }
            case "textboxRevenueCode": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("revenue_code_id"), "lme/ar/RevenueCodeQuickInfo");
            }
            case "textboxOrderType": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("order_type_id"), "lme/powerbroker/OrderTypeQuickInfo");
            }
            case "textboxLastCallinEta": {
                if (!modelRow.getBoolean("brokerage", false)) return;
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    {
                        caption: "Last Callin Date",
                        text: modelRow.get("last_callin_date"),
                        displayType: DisplayType.DATETIME
                    },
                    { caption: "Last Callin City/State", text: modelRow.get("last_callin_loc") },
                    { caption: "Last Callin Comment", text: modelRow.get("last_callin_comment") },
                    { caption: "Last Callin User", text: modelRow.get("last_callin_user") },
                    { caption: "Next Callin Date", text: modelRow.get("next_sched_callin_date") },
                    { caption: "Distance to Next Stop", text: modelRow.get("dist_to_next_stop") }
                ]);
            }
            case "textboxLastCallinDate":
            return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                {
                    caption: "Last Callin Date",
                    text: modelRow.get("last_callin_date"),
                    displayType: DisplayType.DATETIME
                },
                { caption: "Last Callin City/State", text: modelRow.get("last_callin_loc") },
                { caption: "Last Callin Comment", text: modelRow.get("last_callin_comment") },
                { caption: "Last Callin User", text: modelRow.get("last_callin_user") },
                { caption: "Next Callin Date", text: modelRow.get("next_sched_callin_date") },
                { caption: "Distance to Next Stop", text: modelRow.get("dist_to_next_stop") }
            ]);
            case "textboxCarrierName": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("override_payee_id"), "lme/powerbroker/CarrierQuickInfo");
            }
            case "textboxDispatcherName": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("dispatcher_id"), "common/UserQuickInfo");
            }
            case "textboxOperationsUser": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("operations_user_id"), "common/UserQuickInfo");
            }
            case "textboxSalespersonName": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("salesperson_id"), "lme/ar/SalespersonQuickInfo");
            }
            case "textboxDriverManager": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver_manager_id"), "common/UserQuickInfo");
            }
            case "textboxDriverLastLocation": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver1_id"), "lme/dispatch/asset/DriverLastLocationQuickInfo");
            }
            case "labelDriver1Name": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver1_id"), "lme/dispatch/asset/DriverQuickInfo");
            }
            case "LabelDriver2Name": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver2_id"), "lme/dispatch/asset/DriverQuickInfo");
            }
            case "labelTractor": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("tractor_number"), "lme/dispatch/asset/TractorQuickInfo");
            }
            case "labelTrailer": {
                return CommonTooltips.setTooltipContentLayout(comp, this.createTrailerModelRow(modelRow), "lme/dispatch/asset/TrailerQuickInfo");
            }
            case "labelOriginApptStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
            }
            case "labelDestinationApptStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
            }
            case "labelOriginAuthStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_auth_status"), "lme/dispatch/AuthorizationStatusQuickInfo");
            }
            case "labelDestinationAuthStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_auth_status"), "lme/dispatch/AuthorizationStatusQuickInfo");
            }
            case "panelOriginLoc": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            }
            case "panelDestLoc": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            }
            case "labelConsRefNbr":
                comp.tooltipDynamicContent = () => modelRow.get("cons_ref_nbr");
                return;
            case "labelBOL":
                comp.tooltipDynamicContent = () => modelRow.get("bol");
                return;
            case "labelOriginApptStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
            }
            case "labelDestinationApptStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
            }
            case "labelOriginAuthStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_auth_status"), "lme/dispatch/AuthorizationStatusQuickInfo");
            }
            case "labelDestinationAuthStatus": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_auth_status"), "lme/dispatch/AuthorizationStatusQuickInfo");
            }
            case "labelCarrier": {
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("override_payee_id"), "lme/powerbroker/CarrierQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            }

        }
    }

    textboxPlanningCommentOnClick(event: ClickEvent) {
        PlanningUtil.textboxPlanningCommentOnClickListener(event);
    }

    textboxPlanningCommentOnDblClick(event: ClickEvent) {
        PlanningUtil.textboxPlanningCommentOnDblClickListener(event);
    }

    textboxPlanningCommentOnChange(event: ChangeEvent) {
        PlanningUtil.textboxPlanningCommentOnChangeListener(event);
    }

    buttonEditCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonEditCommentOnClickListener(event);
    }

    async buttonSaveCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonSaveCommentOnClickListener(event);
    }

    buttonCancelCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonCancelCommentOnClickListener(event);
    }

    panelBrkStatusOnClick(event: ClickEvent) {
        const panel = event.target as Panel;
        if (panel["statusDropDownItems"] != null)
            displayBrkStatusDropDowns(panel);
        else {
            const row = TableRow.getContainingTableRow(panel);
            populateAndDisplayBrkStatusDropDowns(panel, row?.data?.get("movement_id"), this.tableCustomerServiceMovements, new Button());
        }
    }

    labelStopsPickDropOnClick(event: ClickEvent) {
        this.showMultiStopSlideout(event);
    }

    imageAppointmentOnClick(event: ClickEvent) {
        this.showMultiStopSlideout(event);
    }

    sourceCustomerServiceMovementsOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let eventChangeType = event.change.type;
        if (eventChangeType != ModelDataChangeType.DELETE && !this.filterObject.dataPasses(event.change.data))
            eventChangeType = ModelDataChangeType.DELETE;
        const changedRow = (event.change.data as ModelRow).createBasicCopy();

        this.mainDataSource.handleDataChange({ type: eventChangeType, data: changedRow, lastUpdateNanos: event.change.lastUpdateNanos });

        if (eventChangeType == ModelDataChangeType.UPDATE) {
            const movementId = changedRow.get("movement_id");
            const selectedRows = this.tableCustomerServiceMovements.selectedRows;
            const existingRow: boolean = this.mainDataSource.data.some(row => row.get("movement_id") === movementId);
            if (!existingRow) {
                this.mainDataSource.addRow(changedRow, null, true);
            } else if (selectedRows.length > 0 && selectedRows.some(row => row.data.get("movement_id") === movementId)) {
                this.layoutCustomerServiceToolPanel.syncToolPanel(this.tableCustomerServiceMovements.selectedRows);
            }
        }
        event.preventDefault();
    }

    async showMultiStopSlideout(event) {
        const label = event.target as Label;
        label.busy = true;
        if (!await Layout.canAccessLayout("lme/dispatch/MultiStop")) {
            CommonDialogs.showError("Access denied to lme/dispatch/MultiStop due to permissions");
            label.busy = false;
            return false;
        }
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        let multiStopLayout: MultiStop;
        const erd = new EditRowDecorator({
            title: `Stop Details - Order ${row.get("order_id")}`,
            layout: multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop,
            layoutLoadListeners: async (event) => {
                await multiStopLayout.mainDataSource.search({ id: row.get("order_id") });
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                multiStopLayout.activeRow.set("calc_target_pay", row.get("calc_target_pay"));
                multiStopLayout.activeRow.set("calc_max_pay", row.get("calc_max_pay"));
                if (row.get("target_pay_method") === "S") {
                    multiStopLayout.textboxTargetPay.captionVisible = false;
                    multiStopLayout.panelMPact.visible = true;
                }
                // Commodity Quick Info
                multiStopLayout.activeRow.set("order_id", row.get("order_id"));
                setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
            },
            width: '80%',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onSave: async () => {
                await multiStopLayout.postChanges();
            },
            onClose: (cancelled: boolean) => multiStopLayout.slideOut(),
            doAfterSlideIn: () => label.busy = false,
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
        return true;
    }

    sourceCustomerServiceMovementsAfterExecution(event: DataSourceExecutionEvent) {
        this.filterPanel.syncFilterVisibility(this.profileRow);
    }

    labelOrigApptOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Label, true);
    }

    labelDestApptOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Label, true);
    }

    cellStopsOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Image, true);
    }

    onRowDataDisplay(tableRow: TableRow) {
        const showDetention = getDetentionControlBoolean("is_active", false);
        const modelRow = tableRow.data;
        const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
        if (orderIdLabel != null) {
            orderIdLabel.addUnderlineWhenMouseOver();
        }
        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        if (manifestIdLabel != null) {
            manifestIdLabel.visible = !modelRow?.isNull("manifest_id");
            const manifestId = modelRow?.get("manifest_id");
            const movementId = modelRow?.get("movement_id");
            ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(manifestIdLabel, movementId, manifestId);
        }
        const stopsPickDropLabel = tableRow.findComponentById("labelStopsPickDrop") as Label;
        if (stopsPickDropLabel != null) {
            stopsPickDropLabel.addUnderlineWhenMouseOver();
            CommonTooltips.setTooltipContentLayout(stopsPickDropLabel, null, "lme/dispatch/StopAppointmentStatusList", { maxWidth: 800, maxHeight: 600 }, { movement_id: modelRow.get("movement_id") });
        }
        const imageServiceFail = tableRow.findComponentById("imageServiceIncident") as Image;
        if (imageServiceFail != null) {
            CommonTooltips.setTooltipContentLayout(imageServiceFail, null, "lme/dispatch/ServiceIncidentQuickInfo", { maxWidth: 800, maxHeight: 600 }, { order_id: modelRow.get("order_id"), "status": "A" });
        }
        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        if (originStopType == "SP" || destStopType == "SD") {
            const imageId = originStopType === "SP" && destStopType === "SD" ? "imageYsplitComplete" : originStopType === "SP" ? "imageYsplitPick" : destStopType === "SD" ? "imageYsplit" : null;
            const imageYsplit = tableRow.findComponentById(imageId) as Image;
            if (imageYsplit != null) {
                imageYsplit.visible = true;
            }
        }
        const labelTempRange = tableRow.findComponentById("labelTempRange") as Label;
        if (labelTempRange != null) {
            labelTempRange.text = modelRow.get("temp_range");
        }
        const imageOnHold = tableRow.findComponentById("imageOnHold") as Button;
        if (imageOnHold != null) {
            imageOnHold.visible = modelRow.getBoolean("hold", false);
            imageOnHold.tooltip = modelRow.getString("hold_reason");
        }

        const imageBrokerage = tableRow.findComponentById("imageBrokerage") as Button;
        if (imageBrokerage != null) {
            imageBrokerage.visible = this.isCompanyTypeBoth && modelRow.getBoolean("brokerage", false);
        }

        tableRow.contextMenuItems = () => CustomerServiceToolPanel.getContextMenuItems(this.layoutCustomerServiceToolPanel, tableRow);
        tableRow.addContextMenuCreationListener((event: ContextMenuCreationEvent) => this.layoutCustomerServiceToolPanel.setAsynchronousOptions(event));
        const panelCommodity = tableRow.findComponentById("panelCommodity") as Panel;
        const imageHazmat = tableRow.findComponentById("imageHazmat") as Image;
        if (imageHazmat != null) {
            imageHazmat.visible = !modelRow.isNull("hazmat_code");
        }

        PlanningUtil.checkAppointmentRequiredConfirmed(tableRow);
        PlanningUtil.checkScheduledLate(tableRow, "origin");
        PlanningUtil.checkScheduledLate(tableRow, "destination");

        if (modelRow.get("hazmat_code") != null) {
            CommonTooltips.setTooltipContentTextboxes(panelCommodity, () => [
                { caption: "Hazmat Code", displayType: DisplayType.CURRENCY, text: modelRow.get("hazmat_code") }
            ])
        }
        tableRow.forEveryChildComponent(comp => {
            if (comp instanceof Textbox && (comp.displayType === DisplayType.CURRENCY || comp.displayType === DisplayType.DECIMAL) && comp.printableLabel != null) {
                comp.printableLabel.align = HorizontalAlignment.LEFT;
            }
            this.setTooltipContent(comp, modelRow)
        });
        PlanningUtil.displayBadges(tableRow);
        PlanningUtil.displayIcons(tableRow);
        setupBrkStatusPanel(tableRow);
        if (this.isActiveMovement(modelRow)) {
            this.minuteRowUpdater(tableRow);
        }
        const brokerageStatus = tableRow.findComponentById("panelBrkStatus") as Panel;
        if (brokerageStatus != null && !modelRow.getBoolean("brokerage")) {
            brokerageStatus.visible = false;
        }
        const textboxDispatcherName = tableRow.findComponentById("textboxDispatcherName") as Textbox;
        if (textboxDispatcherName != null) {
            textboxDispatcherName.caption = modelRow.getBoolean("brokerage") ? "Carrier Sales Rep" : "Dispatcher";
        }
        const originApptStatus = tableRow.findComponentById("labelOriginApptStatus") as Label;
        if (originApptStatus != null) {
            originApptStatus.visible = !modelRow.isNull("origin_appt_status");
            originApptStatus.backgroundColor = modelRow.getString("origin_appt_status_color");
            originApptStatus.text = modelRow.getString("origin_appt_status");
        }
        const destinationApptStatus = tableRow.findComponentById("labelDestinationApptStatus") as Label;
        if (destinationApptStatus != null) {
            destinationApptStatus.visible = !modelRow.isNull("destination_appt_status");
            destinationApptStatus.backgroundColor = modelRow.getString("destination_appt_status_color");
            destinationApptStatus.text = modelRow.getString("destination_appt_status");
        }
        if (showDetention) {
            const originAuthStatus = tableRow.findComponentById("labelOriginAuthStatus") as Label;
            if (originAuthStatus != null) {
                originAuthStatus.visible = !modelRow.isNull("origin_detention")
                                         && !("C" == modelRow.getString("origin_detention"))
                                         && !modelRow.isNull("origin_auth_status");
                originAuthStatus.backgroundColor = modelRow.getString("origin_auth_status_color");
                originAuthStatus.text = modelRow.getString("origin_auth_status");
            }
            const destinationAuthStatus = tableRow.findComponentById("labelDestinationAuthStatus") as Label;
            if (destinationAuthStatus != null) {
                destinationAuthStatus.visible = !modelRow.isNull("destination_detention")
                                            && !("C" == modelRow.getString("destination_detention"))
                                            && !modelRow.isNull("destination_auth_status");
                destinationAuthStatus.backgroundColor = modelRow.getString("destination_auth_status_color");
                destinationAuthStatus.text = modelRow.getString("destination_auth_status");
            }
        }
        const labelETAToNextStop = tableRow.findComponentById("labelETAToNextStop") as Label;
        if (labelETAToNextStop != null) {
            labelETAToNextStop.visible = !modelRow.isNull("last_callin_eta");
        }

        const textboxLastCallinEta = tableRow.findComponentById("textboxLastCallinEta") as Textbox;
        if (textboxLastCallinEta != null && textboxLastCallinEta.hasText() && modelRow.get("next_stop_timezone") != null) {
            textboxLastCallinEta.text = DisplayValue.getDisplayValue(modelRow.getDateTime("last_callin_eta"), DisplayType.DATETIME) + " " + modelRow.get("next_stop_timezone");
        }

        const labelStopsPickDrop = tableRow.findComponentById("labelStopsPickDrop") as Label;
        if (labelStopsPickDrop != null) {
            labelStopsPickDrop.applyLinkStyle();
            labelStopsPickDrop.applyButtonHoverStyle();
            CommonTooltips.setTooltipContentLayout(labelStopsPickDrop, null, "lme/dispatch/StopAppointmentStatusList", { maxWidth: 800, maxHeight: 600 }, { movement_id: modelRow.get("movement_id") });
        }

        const textboxPlanningComment = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        if (textboxPlanningComment != null) {
            const saveButton = tableRow.findComponentById("buttonSavePlanningComment") as Button;
            textboxPlanningComment.addKeyDownListener((event: KeyEvent) => PlanningUtil.savePlanningCommentOnKeyDown(event, saveButton));
        }
        PlanningUtil.setDroppedTrailerFont(tableRow, modelRow);
    }

    private updateLateBadges(tableRow: TableRow) {
        if (this.isActiveMovement(tableRow.data)) {
            PlanningUtil.checkScheduledLate(tableRow, "origin");
            PlanningUtil.checkScheduledLate(tableRow, "destination");
        }
    }

    private minuteRowUpdater(tableRow: TableRow) {
        this.updateLateBadges(tableRow);
    }

    public async minuteUpdater() {
        for (const tableRow of this.tableCustomerServiceMovements.rows) {
            if (!tableRow.populatedDOM)
                return;
            this.minuteRowUpdater(tableRow);
        }
    }

    private setMinuteTimer() {
        this.timerId = window.setTimeout(() => this.executeMinuteUpdates(), (60 - new Date().getSeconds()) * 1000);
    }

    private setupMinuteTimer() {
        log.debug("Starting minute timer");
        this.setMinuteTimer();
        this.addUnmountListener(() => this.clearMinuteTimer());
    }

    private executeMinuteUpdates() {
        this.minuteUpdater().then(() => {
            this.setMinuteTimer();
        });
    }

    private clearMinuteTimer() {
        log.debug("Clearing minute timer");
        clearTimeout(this.timerId);
        this.timerId = null;
    }


    imageLoadboardOnClick(event: ClickEvent) {
        const clickedRow = TableRow.getContainingTableRow(event.target as Component);
        const orderId = clickedRow.data.get("order_id");
        PlanningUtil.updateLoadboardFlag(orderId);
    }


    openStatusSlideout(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + id,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.sourceEdistatus.search({ order_id: id });
                    layout.sourceEdiOrder.search({ order_id: id, movement_id: moveId });
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const dataLayout = layout.outboundDataSource.data;
                            dataLayout?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                }
                            });
                        }
                    });
                }
            });
            layout.orderId = id;
            layout.movementId = moveId;
            layout.customerId = customerId;
        }
    }

    openICSlideout(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
            const props = {
                orderId: id,
                moveId: moveId,
                customerId: customerId,
            };
            PlanningUtil.openICSlideout(props);
    }

    private isActiveMovement(row: ModelRow): boolean {
        return ["A", "C", "P"].includes(row.get("move_status"));
    }

    badgeBrTrackStatusOnDataDisplay(event: DataDisplayEvent) {
        const badgeBrTrackStatus = event.target as Label;
        const brTrackStatusForBadge = event.rowData?.get("br_track_status_for_badge");
        badgeBrTrackStatus.text = brTrackStatusForBadge || "--";
        badgeBrTrackStatus.backgroundColor = event.rowData?.get("br_track_status_color");
        badgeBrTrackStatus.tooltipDynamicContent = () => new BrTrackingQuickInfoPanel(event.rowData);
    }

    badgeBrTrackStatusOnDblClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const brDetailsLink: string = row.data.get("br_details_link");
        if (!StringUtil.isEmptyString(brDetailsLink)) {
            window.open(brDetailsLink, "_blank");
        }
    }

    onClickDataDisplay(filter: SecondaryFilter, table: Table) {
        if (!filter.active) {
            table.rows.forEach(tableRow => {
                const displayEvent = new TableRowDisplayEvent(tableRow, table);
                table.fireRowDisplayListeners(displayEvent);
            });
        }
    }

    private applyUrlParams() {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        this.shipmentStatusError = urlParams?.["shipmentStatusError"];
    }

    private setSecondaryFilters() {
        if (this.shipmentStatusError) {
            this.findComponentById("btn" + CustomerServiceSecondaryFilter.SHIPMENT_STATUS_ERRORS.id).clicked();
        }
    }

    private createTrailerModelRow(modelRow: ModelRow): ModelRow {
        if (modelRow.isNull("trailer_number")) {
            return null;
        }
        const serviceStatus = modelRow.get("trailer_active");
        const trailerEmpty = modelRow.get("trailer_empty") || "N";
        const trailerDropped = modelRow.get("trailer_dropped") ? "Yes" : "No";
        
        const serviceStatusMap = { "A": "Active", "I": "Inactive", "S": "In Shop" };
        const emptyMap = { "N": "No", "Y": "Yes" };
        const ownershipMap = { "O": "Owned", "B": "Borrowed" };
        
        const trailerData = {
            id: modelRow.get("trailer_number"),
            trailer_dropped: trailerDropped,
            is_active: serviceStatus,
            empty: trailerEmpty,
            reefer_id: modelRow.get("reefer_id"),
            ownership: modelRow.get("trailer_ownership"),
            trailer_type: modelRow.get("trailer_type"),
            _lookup_is_active: {
                id: serviceStatus,
                descr: serviceStatusMap[serviceStatus] || serviceStatus
            },
            _lookup_empty: {
                id: trailerEmpty,
                descr: emptyMap[trailerEmpty] || trailerEmpty
            },
            _lookup_trailer_type: {
                id: modelRow.get("trailer_type"),
                descr: modelRow.get("trailer_type_descr")
            },
            _lookup_ownership: {
                id: modelRow.get("trailer_ownership"),
                descr: ownershipMap[modelRow.get("trailer_ownership")] || modelRow.get("trailer_ownership")
            },
            _lookup_reefer_id: {
                id: modelRow.get("reefer_id")
            }
        };
        
        return new ModelRow("lme/dispatch/asset/trailer-lookup", false, trailerData);
    }

}
